// A field worker's own compliance file, read-only (round 27).
//
// ALWAYS AVAILABLE. Zanetto Tools puts its equivalent behind a company toggle
// that defaults to off, and its own code calls that toggle "a rollout tool
// rather than a security boundary" — so it is not ported. This is the worker's
// own licences and inductions, they already see their own documents on the
// phone, and a switch whose only job is to hide somebody's tickets from them
// is a setting that will be set wrong.
//
// Read-only, with no exception. Compliance evidence is entered by whoever
// verified it, not by the person it is about — so there is no button here at
// all, and the server refuses a worker's write regardless of what this page
// draws.
import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../api';
import { people, type PersonRecord } from '../peopleApi';
import { Banner } from '../ui';
import { PageHeader, StatusBadge } from '../components/ui';
import { NeedsAttentionCard } from '../components/people/NeedsAttentionCard';
import { PERSON_SECTIONS } from '../lib/personSections';
import { formatDMY } from '../lib/dates';

export function MyRecordPage() {
  const [resource, setResource] = useState<{
    id: string;
    name: string;
    dateOfBirth: string | null;
    employmentStartDate: string | null;
  } | null>(null);
  const [records, setRecords] = useState<PersonRecord[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await people.myRecord();
      setResource(result.resource);
      setRecords(result.records);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your record.');
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (!loaded) return <p className="muted">Loading…</p>;

  return (
    <>
      <PageHeader
        title="My record"
        subtitle="The licences, tickets and inductions the office has on file for you."
      />

      {error && <Banner kind="error">{error}</Banner>}

      {resource === null ? (
        <section className="panel">
          <p className="muted">
            Your account is not linked to anybody on the schedule yet, so there is no record to
            show. Your manager can link it.
          </p>
        </section>
      ) : (
        <>
          <NeedsAttentionCard records={records} isSelf onJumpTo={() => undefined} />

          {/* What the office holds about you personally (round 37) — shown
              because you can see your own file, and omitted entirely when
              your company records neither, which is the ordinary case. */}
          {(resource.dateOfBirth || resource.employmentStartDate) && (
            <section className="panel">
              <h2>About you</h2>
              <dl className="details">
                {resource.dateOfBirth && (
                  <>
                    <dt>Date of birth</dt>
                    <dd>{formatDMY(resource.dateOfBirth)}</dd>
                  </>
                )}
                {resource.employmentStartDate && (
                  <>
                    <dt>Started</dt>
                    <dd>{formatDMY(resource.employmentStartDate)}</dd>
                  </>
                )}
              </dl>
              <p className="hint">
                Your manager keeps these. Ask the office if either is wrong.
              </p>
            </section>
          )}

          {PERSON_SECTIONS.filter((section) => section.recordCategory).map((section) => {
            const mine = records.filter((r) => r.category === section.recordCategory);
            return (
              <section key={section.key} className="panel">
                <h2>
                  {section.label}
                  {mine.length > 0 && <span className="muted"> ({mine.length})</span>}
                </h2>

                {mine.length === 0 ? (
                  <p className="muted">{section.empty}</p>
                ) : (
                  <ul className="m-0 list-none space-y-2 p-0">
                    {mine.map((record) => (
                      <li
                        key={record.id}
                        className="border-t border-gray-200 pt-2 first:border-0 first:pt-0"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{record.title}</span>
                          {record.expiresOn ? (
                            <StatusBadge kind="expiry" status={record.expiry} />
                          ) : (
                            <span className="text-xs text-gray-500">No expiry</span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600">
                          {[
                            record.recordNumber && `No. ${record.recordNumber}`,
                            record.issueDate && `Issued ${formatDMY(record.issueDate)}`,
                            record.expiresOn && `Expires ${formatDMY(record.expiresOn)}`,
                          ]
                            .filter(Boolean)
                            .join(' · ') || <span className="muted">—</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}

          {/* No "renewals are recorded by the office" line here: the needs-
              attention card already says it, and it only means anything when
              something actually needs renewing. Two copies of it on one screen
              is how a sentence stops being read. */}
        </>
      )}
    </>
  );
}
